import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Map as LeafletMap, Marker as LeafletMarker } from "leaflet";
import "leaflet/dist/leaflet.css";
import Ic from "./icons.jsx";

/** Obrenovac plant — used when nothing better is known. */
export const DEFAULT_CENTER = { lat: 44.6547, lng: 20.2003 };

type Coords = { lat: number; lng: number };

type Props = {
  lat: number | null;
  lng: number | null;
  /** Called on map click, marker drag, geolocation or place search. Absent in read-only mode. */
  onChange?: (coords: Coords) => void;
  /** Where to center when there is no point yet (e.g. parent's effective GPS). */
  fallbackCenter?: Coords | null;
  readOnly?: boolean;
  height?: number;
};

/**
 * OpenStreetMap picker built on Leaflet. Leaflet itself is loaded lazily so it stays out of
 * the main bundle. The marker is a CSS div icon (Leaflet's PNG icons don't survive bundling
 * without extra config). Tiles come from tile.openstreetmap.org — the plant tablets need
 * internet for the map to render; coordinates still work without it.
 */
export function MapPicker({ lat, lng, onChange, fallbackCenter, readOnly = false, height = 320 }: Props) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const markerRef = useRef<LeafletMarker | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // Fullscreen (Strahinja, 2026-09-11: every map can open over the whole screen): the same map
  // element is re-laid out as a fixed overlay, Leaflet just gets told the size changed.
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    // Body flag: ancestors that create a stacking context (sticky columns) are neutralised in CSS
    // while a map is fullscreen, otherwise the fixed overlay stays trapped under the topbar.
    document.body.classList.toggle("has-map-fullscreen", fullscreen);
    const id = window.setTimeout(() => mapRef.current?.invalidateSize(), 60);
    if (!fullscreen) return () => window.clearTimeout(id);
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") { e.stopPropagation(); setFullscreen(false); }
    }
    document.addEventListener("keydown", onKey, true);
    return () => { window.clearTimeout(id); document.removeEventListener("keydown", onKey, true); };
  }, [fullscreen]);
  useEffect(() => () => document.body.classList.remove("has-map-fullscreen"), []);

  // Create the map once.
  useEffect(() => {
    let cancelled = false;
    let map: LeafletMap | null = null;
    (async () => {
      try {
        const L = await import("leaflet");
        if (cancelled || !containerRef.current) return;
        const start = lat !== null && lng !== null ? { lat, lng } : (fallbackCenter ?? DEFAULT_CENTER);
        const zoom = lat !== null && lng !== null ? 16 : fallbackCenter ? 15 : 13;
        map = L.map(containerRef.current, { zoomControl: !readOnly, dragging: true, scrollWheelZoom: !readOnly });
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        }).addTo(map);
        map.setView([start.lat, start.lng], zoom);

        const icon = L.divIcon({ className: "map-marker", iconSize: [22, 22], iconAnchor: [11, 11] });
        const marker = L.marker([start.lat, start.lng], { icon, draggable: !readOnly, keyboard: false });
        if (lat !== null && lng !== null) marker.addTo(map);
        marker.on("dragend", () => {
          const p = marker.getLatLng();
          onChangeRef.current?.({ lat: round(p.lat), lng: round(p.lng) });
        });
        if (!readOnly) {
          map.on("click", (e) => {
            const p = e.latlng;
            if (!map!.hasLayer(marker)) marker.addTo(map!);
            marker.setLatLng(p);
            onChangeRef.current?.({ lat: round(p.lat), lng: round(p.lng) });
          });
        }
        mapRef.current = map;
        markerRef.current = marker;
        setReady(true);
        // The container may have been laid out after Leaflet measured it (modal animation).
        setTimeout(() => map?.invalidateSize(), 50);
      } catch {
        setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
      map?.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the marker in sync when the coordinates are typed by hand.
  useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;
    if (lat === null || lng === null) {
      if (map.hasLayer(marker)) map.removeLayer(marker);
      return;
    }
    if (!map.hasLayer(marker)) marker.addTo(map);
    const cur = marker.getLatLng();
    if (Math.abs(cur.lat - lat) > 1e-7 || Math.abs(cur.lng - lng) > 1e-7) {
      marker.setLatLng([lat, lng]);
      map.panTo([lat, lng]);
    }
  }, [lat, lng, ready]);

  return (
    <div className={`map-picker${fullscreen ? " is-fullscreen" : ""}`} style={fullscreen ? undefined : { height }}>
      <div ref={containerRef} className="map-picker-canvas" aria-label={t("map.aria", "Mapa")} />
      {loadError && <div className="map-picker-error">{t("map.loadError", "Mapa nije mogla da se učita.")}</div>}
      {!loadError && (
        <button
          type="button"
          className="map-picker-fs"
          onClick={() => setFullscreen((f) => !f)}
          title={fullscreen ? t("map.exitFullscreen", "Zatvori ceo ekran (Esc)") : t("map.fullscreen", "Ceo ekran")}
          aria-label={fullscreen ? t("map.exitFullscreen", "Zatvori ceo ekran (Esc)") : t("map.fullscreen", "Ceo ekran")}
          aria-pressed={fullscreen}
        >
          {fullscreen ? <Ic.x /> : <Ic.fit />}
        </button>
      )}
    </div>
  );
}

/** Browser geolocation → coords, or a Serbian error message. */
export function locateMe(): Promise<Coords> {
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      reject(new Error("Uređaj nema GPS / geolokaciju."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: round(pos.coords.latitude), lng: round(pos.coords.longitude) }),
      (err) => reject(new Error(err.code === err.PERMISSION_DENIED
        ? "Pristup lokaciji je odbijen u pretraživaču."
        : "Lokacija trenutno nije dostupna.")),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  });
}

export type PlaceHit = { label: string; lat: number; lng: number };

/** Free-text place search via OpenStreetMap Nominatim (Serbia first). Best-effort, may be blocked offline. */
export async function searchPlace(q: string): Promise<PlaceHit[]> {
  const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&accept-language=sr-Latn&countrycodes=rs&q=${encodeURIComponent(q)}`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error("Pretraga mesta nije uspela.");
  const rows = (await res.json()) as { display_name: string; lat: string; lon: string }[];
  return rows.map((r) => ({ label: r.display_name, lat: round(Number(r.lat)), lng: round(Number(r.lon)) }));
}

function round(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}
